import { PropsWithChildren, ReactNode, useRef } from 'react';

import {
  Icons,
  LabelChip,
  Radio,
  Skeleton,
  Tooltip,
  Typography,
  Box,
  Stack,
  ChoicePanel,
  tokens,
} from '@cast/design-system';

import { AvailableKentContinuousRebalancingConfigMode } from '../_types';

const modeToTitleMap: Record<
  AvailableKentContinuousRebalancingConfigMode,
  string
> = {
  EMPTY_NODE_REMOVAL: 'Empty node removal',
  BIN_PACKING: 'Bin-packing',
  CONTINUOUS_REBALANCING_CYCLE: 'Continuous rebalancing',
};

const modeToDescriptionMap: Record<
  AvailableKentContinuousRebalancingConfigMode,
  string
> = {
  EMPTY_NODE_REMOVAL:
    'Removes nodes with no running workloads, keeping your cluster free of idle capacity.',
  BIN_PACKING:
    'Compacts workloads onto fewer nodes, freeing up and removing underutilized ones.',
  CONTINUOUS_REBALANCING_CYCLE:
    'Continuously replaces nodes with cheaper, better-fit alternatives on top of bin-packing.',
};

const emptyNodeRemovalFeatureEnabledMap: Record<
  AvailableKentContinuousRebalancingConfigMode,
  boolean
> = {
  EMPTY_NODE_REMOVAL: true,
  BIN_PACKING: true,
  CONTINUOUS_REBALANCING_CYCLE: true,
};

const binPackingFeatureEnabledMap: Record<
  AvailableKentContinuousRebalancingConfigMode,
  boolean
> = {
  EMPTY_NODE_REMOVAL: false,
  BIN_PACKING: true,
  CONTINUOUS_REBALANCING_CYCLE: true,
};

const nodeRebalancingFeatureEnabledMap: Record<
  AvailableKentContinuousRebalancingConfigMode,
  boolean
> = {
  EMPTY_NODE_REMOVAL: false,
  BIN_PACKING: false,
  CONTINUOUS_REBALANCING_CYCLE: true,
};

type ModeFeatureProps = {
  included: boolean;
  label: string;
};

const ModeFeature = ({ included, label }: ModeFeatureProps) => {
  return (
    <Stack direction="row" gap={4} alignItems="center">
      {included ? (
        <Icons.CheckCircle
          size={16}
          weight="fill"
          color={tokens.theme.bg.categorical.greenStrong}
        />
      ) : (
        <Icons.MinusCircle
          size={16}
          weight="fill"
          color={tokens.theme.bg.categorical.greyStrong}
        />
      )}
      <Typography
        variant="P12R"
        color={included ? undefined : tokens.theme.fg.structural.secondary}
      >
        {label}
      </Typography>
    </Stack>
  );
};

type ContainerProps = PropsWithChildren<{
  testId?: string;
  onClick?: () => void;
  selected?: boolean;
  disabled?: boolean;
}>;

const Container = ({
  children,
  testId,
  onClick,
  selected,
  disabled,
}: ContainerProps) => {
  return (
    <ChoicePanel
      testId={testId}
      onClick={onClick}
      selected={selected}
      disabled={disabled || !onClick}
      sx={{
        height: '100%',
        position: 'relative',
        cursor: 'pointer',
        p: 16,
      }}
    >
      {children}
    </ChoicePanel>
  );
};

type ConsolidationMethodProps = {
  mode: AvailableKentContinuousRebalancingConfigMode;
  tooltipContent?: ReactNode;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
};

export const ConsolidationMethod = ({
  mode,
  selected,
  disabled,
  tooltipContent,
  onSelect,
}: ConsolidationMethodProps) => {
  const radioRef = useRef<HTMLButtonElement>(null);

  return (
    <Tooltip
      placement="top"
      title={tooltipContent}
      wrapIf={!!tooltipContent}
      PopperProps={{ anchorEl: radioRef.current }}
    >
      <Box>
        <Container
          testId={`consolidation-method-${mode}`}
          onClick={onSelect}
          selected={selected}
          disabled={disabled}
        >
          <Stack direction="row" gap={8}>
            <Radio
              ref={radioRef}
              testId={`consolidation-method-${mode}-radio`}
              size="medium"
              checked={selected}
              disabled={disabled}
            />

            <Stack gap={12}>
              <Stack direction="row" gap={8} alignItems="center">
                <Typography variant="A1">{modeToTitleMap[mode]}</Typography>
                {mode === 'CONTINUOUS_REBALANCING_CYCLE' && (
                  <LabelChip
                    size="sm"
                    sx={{
                      bgcolor: tokens.theme.bg.categorical.indigoStrong,
                      color: tokens.theme.fg.categorical.onIndigoStrong,
                    }}
                  >
                    Recommended
                  </LabelChip>
                )}
              </Stack>
              <Typography
                variant="P12R"
                color={tokens.theme.fg.structural.secondary}
              >
                {modeToDescriptionMap[mode]}
              </Typography>

              <Stack gap={8}>
                <ModeFeature
                  included={emptyNodeRemovalFeatureEnabledMap[mode]}
                  label="Empty node removal"
                />
                <ModeFeature
                  included={binPackingFeatureEnabledMap[mode]}
                  label="Bin-packing"
                />
                <ModeFeature
                  included={nodeRebalancingFeatureEnabledMap[mode]}
                  label="Node rebalancing"
                />
              </Stack>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Tooltip>
  );
};

const ConsolidationMethodSkeleton = () => {
  return (
    <Container>
      <Skeleton height="100%" width="100%" />
    </Container>
  );
};

ConsolidationMethod.Skeleton = ConsolidationMethodSkeleton;
